import { isUnifiedCardStack } from "@shared/lib/card-stack/unified-migration"
import type { FindFn } from "@shared/models/analytics/base"
import { createBlankEvent } from "@shared/models/analytics/base"
import type { LessonEditCard } from "@shared/models/analytics/lesson"
import { getPossessiveForPlanbook } from "@shared/models/analytics/planbook"
import type { CardStack } from "@shared/models/card-stack"
import type { Course } from "@shared/models/course"
import type { Planbook } from "@shared/models/planbook"
import Patch from "@shared/models/patch-updater"
import { cloneDeep, get } from "lodash-es"
import moment from "moment"
import type { Narrative } from "../create-narrative"
import defaultLessonActivity from "./default-lesson-activity"
import defaultLessonContext from "./default-lesson-context"
import defaultLessonModels from "./default-lesson-models"

// TODO This needs to be relooked at

interface Payload {
  course: Course
  planbook: Planbook
  dateString: string
  /**
   * Our local copy of the CardStack whose cards are being created.
   *
   * The CardStack's cards only exist in memory, not in the DB (isNewLesson is true);
   * they were created by the PhantomCardStack service.
   * This action creates them in the DB, by issuing patches
   * that are redundant locally.
   */
  cardStack: CardStack
}
export const LESSON_FIRST_CARDS_EDIT = {
  name: "LESSON_FIRST_CARDS_EDIT",

  patches(payload: Payload) {
    const { cardStack } = payload
    const cards = cardStack.attributes.cards ?? []
    let patch = Patch.create("card-stack", cardStack.id)
      .set("attributes.cards", cloneDeep(cards))
      .set("relationships.course.data", {
        id: payload.course.id,
        type: "course",
      })
      .set("relationships.planbook.data", {
        id: payload.planbook.id,
        type: "planbook",
      })
      .set("meta.cardsUpdatingFromTemplate", false) // Internal flag we use when the container-lesson sets the templates

    if (isUnifiedCardStack(cardStack)) {
      patch = patch
        .set("attributes.proseMirror", cloneDeep(cardStack.attributes.proseMirror))
        .set("attributes.extracted", cloneDeep(cardStack.attributes.extracted))
    }

    return [patch.patch]
  },

  undoPatches(payload: Payload) {
    return [
      Patch.create("card-stack", payload.cardStack.id)
        .set("attributes.cards", null)
        .set("meta.cardsUpdatingFromTemplate", true).patch, // Internal flag we use when the container-lesson sets the templates
    ]
  },

  narrative(payload: Payload, findFn: FindFn, userId): Narrative {
    let models = defaultLessonModels(
      { ...payload, cardStackId: payload.cardStack.id },
      findFn,
      userId
    )
    let context = defaultLessonContext(payload, models)
    context.editType = "template"

    let formattedDateString = moment(payload.dateString).format("ddd, MMM. D, YYYY")

    let newEvent: LessonEditCard = {
      ...createBlankEvent(),
      root_id: models.planbook?.id ?? null,
      root_type: "planbook",
      date_plan_scheduled_for: models.dateStringForCardStack,
      primary_object_id: payload.cardStack.id,
      primary_object_id_and_ancestor_ids: models.cardStackIdAndAncestorIds,
      primary_verb: "edit",
      primary_possessive: getPossessiveForPlanbook(findFn, userId, models.planbook?.id),
      primary_object_type: "lesson",
      primary_object_specifier: "cards",

      adverbial_verb: null,
      adverbial_object_type: null,
      adverbial_object_specifier: null,
      adverbial_object_id: null,
      adverbial_possessive: null,
      adverbial_object_id_and_ancestor_ids: null,
    }

    return {
      title: "Edit the Cards on a lesson",
      titleForAnalytics: "Lesson - Edited Cards",
      description: `Edited a lesson on ${formattedDateString} to ${get(
        models,
        "course.attributes.title"
      )}`,
      context: defaultLessonContext(payload, models),
      activity: defaultLessonActivity(payload, models, "created"),
      newAnalyticsEvents: [newEvent],
    }
  },
}

export default LESSON_FIRST_CARDS_EDIT
